import type { DefaultError, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';

import { customToast } from '@/components/Toast';
import { useDucatClient } from '@/context/DucatClientCtx';
import { useAppStore } from '@/stores/useAppStore';

import { QueryKeys } from '../query-keys';

import type { QuantaHeaders } from './auth';
import { quantaHeadersBelongToWallet } from './quantaIdentity';
import type { CompletedTask } from './useQuantaMissionTasks';
import type { Referral } from './useQuantaRegister';

export interface Level {
  level_one: number;
  level_two: number;
  level_three: number;
  // Optional: only present once the backend exposes the level-4 (Black Hole) ceiling.
  level_four?: number;
}

export interface RegisterUser {
  user_id: string;
  test_net_wallet: string;
  test_net_pk: string;
}

export interface QuantaUser extends RegisterUser {
  discord_id: string | null;
  twitter_id: string | null;
  telegram_id: string | null;
  matrica_id: string | null;
  vault_id: string;
  level: number;
}

export interface QuantaLoginResponse {
  data: {
    user: QuantaUser;
    referral_id: string;
    referral: Referral;
    points_total: number;
    borrow_accrual?: {
      accrued_points: number;
      last_unit_debt: number;
      last_accrued_at: string;
      eligible?: boolean;
      network?: 'mainnet' | 'testnet' | null;
    } | null;
    ranking: number | null;
    referred_users: number;
    referral_points: number;
    referral_reward?: number;
    completed_tasks_count: number;
    completed_tasks: CompletedTask[];
    levels: Level;
  };
}

// Cache a fresh proof separately without copying a bearer credential into diagnostics.
const sessionIds = new WeakMap<QuantaHeaders, number>();
let nextSessionId = 0;
const reportedFailures = new WeakSet<QuantaHeaders>();
function sessionId(headers: QuantaHeaders | undefined) {
  if (!headers) return 0;
  if (!sessionIds.has(headers)) sessionIds.set(headers, ++nextSessionId);
  return sessionIds.get(headers);
}

type QueryOptionsParam = Omit<UseQueryOptions<QuantaLoginResponse, DefaultError>, 'queryKey' | 'queryFn'>;

export const fetchQuantaLogin = async (headers: QuantaHeaders): Promise<QuantaLoginResponse> => {
  const { quantaApi } = await import('./quantaApi');
  const result = await quantaApi.get('/login', { headers });
  return result.data;
};

export const useQuantaLogin = (options?: QueryOptionsParam): UseQueryResult<QuantaLoginResponse, Error> => {
  const { wallet, pubkey, accountRevision } = useDucatClient();
  const t = useTranslations('quanta.page');
  const headers = useAppStore((s) => s.quantaHeaders);
  const belongsToWallet = quantaHeadersBelongToWallet(headers, wallet, accountRevision);
  const hasQuantaHeaders = Boolean(
    belongsToWallet &&
      headers?.['x-address'] &&
      headers?.['x-user-public-key'] &&
      headers?.['x-hash'] &&
      headers?.['x-value'] &&
      headers?.['x-signed-message'],
  );
  return useQuery({
    queryKey: [
      QueryKeys.quantaLogin,
      ...(belongsToWallet ? [headers?.['x-address'], headers?.['x-user-public-key'], sessionId(headers)] : ['disconnected', pubkey]),
    ],
    queryFn: async () => {
      if (!headers || !hasQuantaHeaders || useAppStore.getState().quantaHeaders !== headers)
        throw new Error('Missing Quanta authentication for the connected wallet');
      try {
        const response = await fetchQuantaLogin(headers);
        reportedFailures.delete(headers);
        return response;
      } catch (error) {
        const status = (error as { response?: { status?: number } }).response?.status;
        // The backend rejects invalid proofs with 400 and unknown users with 404.
        // A CDN can return 403 without invalidating the wallet's proof.
        const code = (error as { response?: { data?: { code?: string } } }).response?.data?.code;
        const terminal = status === 400 || status === 401 || (status === 404 && code === 'NOT_FOUND');
        if (useAppStore.getState().quantaHeaders === headers) {
          const onQuantaPage = typeof window !== 'undefined' && window.location.pathname.split('/').includes('quanta');
          if (onQuantaPage && (terminal || !reportedFailures.has(headers))) customToast.error(t('loginFailed'));
          if (terminal) {
            reportedFailures.delete(headers);
            useAppStore.getState().setQuantaHeaders(undefined);
          } else {
            reportedFailures.add(headers);
          }
        }
        throw error;
      }
    },
    staleTime: 0, // made on purpose
    retry: false,
    ...options,
    enabled: hasQuantaHeaders && (options?.enabled === undefined || !!options?.enabled),
  });
};
